<template>
  <slide-over :open="open" :title="'My messages'" @changed="changed">
    <template v-slot:contents>
      <div v-if="!notifications.length">
        <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg
                class="h-5 w-5 text-yellow-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm leading-5 text-yellow-700">
                You currently have no notifications to display.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div class="flex flex-row-reverse">
          <button
            @click="readAllNotification"
            :disabled="isReadAllLoading"
            type="button"
            class="py-2.5 px-5 mr-2 text-sm font-medium text-gray-900 bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:outline-none focus:ring-blue-700 focus:text-blue-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700 inline-flex items-center"
          >
            <svg
              v-if="isReadAllLoading"
              aria-hidden="true"
              role="status"
              class="inline w-4 h-4 mr-3 text-gray-200 animate-spin dark:text-gray-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="#1C64F2"
              />
            </svg>
            <span v-if="!isReadAllLoading">Read All</span>
            <span v-else>Loading...</span>
          </button>
        </div>
        <div v-for="notification in notifications" :key="notification.id">
          <div
            class="flex items-end justify-center px-1 py-4 pointer-events-none sm:items-start sm:justify-end w-full"
          >
            <div
              :class="{
                'shadow-lg': !notification.read,
                'opacity-75': notification.read,
              }"
              class="w-full rounded-lg pointer-events-auto border border-gray-100"
            >
              <div class="rounded-lg shadow-xs overflow-hidden">
                <div class="p-4">
                  <div class="flex items-start">
                    <div class="flex-shrink-0">
                      <svg
                        v-if="notification.type === 'success'"
                        class="h-6 w-6 text-green-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <svg
                        v-else-if="notification.type === 'error'"
                        class="w-6 h-6 text-red-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                      <svg
                        v-else
                        class="w-6 h-6 text-blue-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                    </div>
                    <div class="ml-3 w-0 flex-1 pt-0.5">
                      <p class="text-sm leading-5 font-medium text-gray-900">
                        <span
                          v-if="notification.type === 'success'"
                          class="text-green-400"
                          >Success</span
                        >
                        <span
                          v-else-if="notification.type === 'error'"
                          class="text-red-400"
                          >Error</span
                        >
                        <span v-else class="text-blue-400">Info</span>
                      </p>
                      <p class="mt-1 text-sm leading-5 text-gray-500">
                        {{ notification.message }}
                      </p>
                      <div class="mt-2">
                        <button
                          @click.prevent="markAsRead(notification)"
                          v-if="!notification.read"
                          class="text-sm leading-5 font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline transition ease-in-out duration-150"
                        >
                          Mark as read
                        </button>
                        <button
                          @click.prevent="deleteNotification(notification)"
                          :class="{ 'ml-6': !notification.read }"
                          class="text-sm leading-5 font-medium text-red-500 hover:text-red-700 focus:outline-none focus:underline transition ease-in-out duration-150"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div class="ml-4 flex-shrink-0 flex text-xs text-gray-500">
                      {{ format(notification.created_at) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </slide-over>
</template>

<script>
import SlideOver from "@/components/SlideOver";
import moment from "moment";

let interval = null;

export default {
  emits: ["changed", "notifications"],
  components: {
    SlideOver,
  },
  data() {
    return {
      isReadAllLoading: false,
    };
  },
  props: {
    open: {
      default: false,
      type: Boolean,
    },
  },
  mounted() {
    if (interval === null) {
      this.getNotifications();
      interval = setInterval(() => {
        this.getNotifications();
      }, 20000);
    }
  },
  computed: {
    notifications() {
      return this.$store.state.messages;
    },
  },
  methods: {
    async readAllNotification() {
      try {
        this.isReadAllLoading = true;
        const res = await this.$axios.post(
          this.$global.apiEndpoint(`/notification/mark-all-as-read`),
          {},
        );
        this.$global.success(res.data);
      } catch (err) {
        console.log(err.response);
        this.$global.error(err.response);
      } finally {
        this.isReadAllLoading = false;
      }
    },
    getNotifications() {
      this.$axios
        .get(this.$global.apiEndpoint("/notification/all"))
        .then(response => {
          this.$store.commit("setMessages", response.data);
        })
        .catch(() => {});
    },
    changed(value) {
      this.$emit("changed", value);
    },
    format(dateTime) {
      return moment(dateTime).format("DD/MM/YYYY HH:mm");
    },
    deleteNotification(notification) {
      this.$axios
        .post(
          this.$global.apiEndpoint(`/notification/${notification.id}/delete`),
          {},
        )
        .then(() => {
          this.$store.commit("removeMessage", notification);
        });
    },
    markAsRead(notification) {
      notification.read = 1;
      this.$axios.post(
        this.$global.apiEndpoint(
          `/notification/${notification.id}/mark-as-read`,
        ),
        {},
      );
    },
  },
};
</script>
